import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Loader2, MapPin, Save, AlertCircle, AlertTriangle, Layers } from 'lucide-react'
import { api } from '@/lib/api'

declare global {
  interface Window {
    google: typeof google
  }
}

export type ProjectLocation = {
  latitude: number
  longitude: number
  address: string
  district: string
  city: string
}

interface ProjectLocationEditorProps {
  latitude?: number | null
  longitude?: number | null
  address?: string
  district?: string
  city?: string
  onLocationChange: (location: ProjectLocation) => void
  onSave: () => void
}

const DEFAULT_CENTER = { lat: 38.7223, lng: -9.1393 }

type PinField = 'address' | 'district' | 'city'

/** A pin fills empty fields; a later move may replace what a pin filled, never a typed value. */
const fillFromPin = (filled: Partial<Record<PinField, string>>, field: PinField, current: string, next: string) => {
  if (!next || (current && current !== filled[field])) return current
  filled[field] = next
  return next
}

const loadGoogleMapsApi = (() => {
  let promise: Promise<void> | null = null

  return (apiKey: string) => {
    if (typeof window !== 'undefined' && window.google?.maps) {
      return Promise.resolve()
    }

    if (!promise) {
      promise = new Promise<void>((resolve, reject) => {
        const script = document.createElement('script')
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`
        script.async = true
        script.defer = true
        script.onload = () => resolve()
        script.onerror = () => reject(new Error('Failed to load Google Maps script'))
        document.head.appendChild(script)
      })
    }

    return promise
  }
})()

export const ProjectLocationEditor: React.FC<ProjectLocationEditorProps> = ({
  latitude,
  longitude,
  address = '',
  district = '',
  city = '',
  onLocationChange,
  onSave,
}) => {
  const mapContainerRef = useRef<HTMLDivElement | null>(null)
  const mapInstanceRef = useRef<google.maps.Map | null>(null)
  const markerRef = useRef<google.maps.Marker | null>(null)
  const listenersRef = useRef<google.maps.MapsEventListener[]>([])
  const pinFilledRef = useRef<Partial<Record<PinField, string>>>({})
  const [apiKey, setApiKey] = useState<string | null>(null)
  const [isLoadingMap, setIsLoadingMap] = useState(true)
  const [isReverseGeocoding, setIsReverseGeocoding] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [geocodeWarning, setGeocodeWarning] = useState<string | null>(null)

  const [location, setLocation] = useState<ProjectLocation>({
    latitude: latitude ?? DEFAULT_CENTER.lat,
    longitude: longitude ?? DEFAULT_CENTER.lng,
    address,
    district,
    city,
  })
  const [manualLatitude, setManualLatitude] = useState<string>(() => (latitude ?? DEFAULT_CENTER.lat).toFixed(6))
  const [manualLongitude, setManualLongitude] = useState<string>(() => (longitude ?? DEFAULT_CENTER.lng).toFixed(6))
  const [manualError, setManualError] = useState<string | null>(null)
  const [mapType, setMapType] = useState<google.maps.MapTypeId>('roadmap' as google.maps.MapTypeId)

  const removeListeners = useCallback(() => {
    listenersRef.current.forEach((listener) => listener.remove())
    listenersRef.current = []
  }, [])

  const extractAdministrativeAreas = useCallback(
    (components: google.maps.GeocoderAddressComponent[]): Pick<ProjectLocation, 'district' | 'city'> => {
      let nextDistrict = ''
      let nextCity = ''

      components.forEach((component) => {
        if (component.types.includes('administrative_area_level_2')) {
          nextDistrict = component.long_name
        }

        if (component.types.includes('locality')) {
          nextCity = component.long_name
        }

        if (!nextCity && component.types.includes('administrative_area_level_1')) {
          nextCity = component.long_name
        }
      })

      return { district: nextDistrict, city: nextCity }
    },
    []
  )

  const reverseGeocode = useCallback(
    async (latLng: google.maps.LatLngLiteral) => {
      if (!window.google?.maps) return

      setIsReverseGeocoding(true)
      setError(null)
      setGeocodeWarning(null)

      try {
        const geocoder = new google.maps.Geocoder()
        const { results } = await geocoder.geocode({ location: latLng })

        if (!results?.length) {
          setGeocodeWarning('No address found for this location. Coordinates saved without updating the address.')
          setLocation((prev) => {
            const updated = {
              ...prev,
              latitude: latLng.lat,
              longitude: latLng.lng,
            }
            onLocationChange(updated)
            return updated
          })
          return
        }

        const preciseTypes = new Set<string>([
          'street_address',
          'premise',
          'subpremise',
          'point_of_interest',
          'establishment',
        ])

        const preciseLocationTypes = new Set<google.maps.GeocoderLocationType>([
          google.maps.GeocoderLocationType.ROOFTOP,
          google.maps.GeocoderLocationType.RANGE_INTERPOLATED,
        ])

        const preciseResult = results.find((result) => {
          const isPreciseType = result.types.some((type) => preciseTypes.has(type))
          const isPreciseLocation = preciseLocationTypes.has(result.geometry.location_type)
          return isPreciseType && isPreciseLocation
        })

        const fallbackResult = preciseResult ?? results[0]

        if (!preciseResult) {
          const { district: fallbackDistrict, city: fallbackCity } = extractAdministrativeAreas(fallbackResult.address_components ?? [])

          setGeocodeWarning('No precise street address was found. Coordinates were kept without modifying the address field.')

          setLocation((prev) => {
            const updated = {
              ...prev,
              latitude: latLng.lat,
              longitude: latLng.lng,
              district: fillFromPin(pinFilledRef.current, 'district', prev.district, fallbackDistrict),
              city: fillFromPin(pinFilledRef.current, 'city', prev.city, fallbackCity),
            }
            onLocationChange(updated)
            return updated
          })
          return
        }

        const { formatted_address, address_components } = preciseResult
        const { district: nextDistrict, city: nextCity } = extractAdministrativeAreas(address_components)

        setGeocodeWarning(null)

        setLocation((prev) => {
          const updated: ProjectLocation = {
            ...prev,
            latitude: latLng.lat,
            longitude: latLng.lng,
            address: fillFromPin(pinFilledRef.current, 'address', prev.address, formatted_address),
            district: fillFromPin(pinFilledRef.current, 'district', prev.district, nextDistrict),
            city: fillFromPin(pinFilledRef.current, 'city', prev.city, nextCity),
          }
          onLocationChange(updated)
          return updated
        })
      } catch (err: any) {
        console.error('Reverse geocoding failed:', err)
        setError(err?.message || 'Unable to resolve address for this location.')
      } finally {
        setIsReverseGeocoding(false)
      }
    },
    [extractAdministrativeAreas, onLocationChange]
  )

  const updateMarkerPosition = useCallback(
    (latLng: google.maps.LatLngLiteral) => {
      const marker = markerRef.current
      const map = mapInstanceRef.current

      if (marker) {
        marker.setPosition(latLng)
      }
      if (map) {
        map.panTo(latLng)
      }

      setLocation((prev) => {
        const updated = { ...prev, latitude: latLng.lat, longitude: latLng.lng }
        onLocationChange(updated)
        return updated
      })
    },
    [onLocationChange]
  )

  const applyManualCoordinates = useCallback(() => {
    const parsedLat = Number.parseFloat(manualLatitude)
    const parsedLng = Number.parseFloat(manualLongitude)

    if (!Number.isFinite(parsedLat) || parsedLat < -90 || parsedLat > 90) {
      setManualError('Latitude must be a number between -90 and 90 degrees.')
      return
    }

    if (!Number.isFinite(parsedLng) || parsedLng < -180 || parsedLng > 180) {
      setManualError('Longitude must be a number between -180 and 180 degrees.')
      return
    }

    const latLng = { lat: parsedLat, lng: parsedLng }

    // Skip if coordinates did not change (rounded to 6 decimals)
    if (
      Math.abs(latLng.lat - location.latitude) < 1e-6 &&
      Math.abs(latLng.lng - location.longitude) < 1e-6
    ) {
      setManualError(null)
      return
    }

    setManualError(null)
    updateMarkerPosition(latLng)

    if (window.google?.maps) {
      reverseGeocode(latLng)
    }
  }, [manualLatitude, manualLongitude, location.latitude, location.longitude, reverseGeocode, updateMarkerPosition])

  const registerInteractions = useCallback(() => {
    const marker = markerRef.current
    const map = mapInstanceRef.current
    if (!marker || !map) return

    removeListeners()

    listenersRef.current.push(
      marker.addListener('dragend', () => {
        const position = marker.getPosition()
        if (!position) return
        const nextLatLng = { lat: position.lat(), lng: position.lng() }
        updateMarkerPosition(nextLatLng)
        reverseGeocode(nextLatLng)
      })
    )

    listenersRef.current.push(
      map.addListener('click', (event: google.maps.MapMouseEvent) => {
        if (!event.latLng) return
        const nextLatLng = event.latLng.toJSON()
        updateMarkerPosition(nextLatLng)
        reverseGeocode(nextLatLng)
      })
    )
  }, [removeListeners, reverseGeocode, updateMarkerPosition])

  useEffect(() => {
    let mounted = true

    const fetchApiKey = async () => {
      try {
        const { data } = await api.get('/geocoding/api-key')
        if (!mounted) return

        if (data?.success && data?.apiKey) {
          setApiKey(data.apiKey)
        } else {
          setError('Google Maps API key unavailable. Please verify configuration.')
        }
      } catch (err) {
        console.error('Error fetching Google Maps API key:', err)
        if (mounted) {
          setError('Failed to load Google Maps API key.')
        }
      }
    }

    fetchApiKey()

    return () => {
      mounted = false
    }
  }, [])

  useEffect(() => {
    const initializeMap = async () => {
      if (!apiKey || !mapContainerRef.current) return

      try {
        setIsLoadingMap(true)
        await loadGoogleMapsApi(apiKey)

        const center = new google.maps.LatLng(location.latitude, location.longitude)

        const map = new google.maps.Map(mapContainerRef.current, {
          center,
          zoom: 15,
          disableDefaultUI: true,
          zoomControl: true,
          gestureHandling: 'greedy',
          mapTypeId: mapType,
        })

        const marker = new google.maps.Marker({
          position: center,
          map,
          draggable: true,
          title: 'Project Location',
        })

        mapInstanceRef.current = map
        markerRef.current = marker

        registerInteractions()
      } catch (err) {
        console.error('Failed to initialize Google Map:', err)
        setError('Failed to initialize Google Map.')
      } finally {
        setIsLoadingMap(false)
      }
    }

    initializeMap()

    return () => {
      removeListeners()
      markerRef.current?.setMap(null)
      markerRef.current = null
      mapInstanceRef.current = null
    }
  }, [apiKey, registerInteractions, removeListeners])

  useEffect(() => {
    setManualLatitude(location.latitude.toFixed(6))
    setManualLongitude(location.longitude.toFixed(6))
    setManualError(null)
  }, [location.latitude, location.longitude])

  // These fields are edited in the form: a pin move must not write back an older copy.
  useEffect(() => {
    setLocation((prev) => ({ ...prev, address, district, city }))
  }, [address, district, city])

  useEffect(() => {
    if (latitude == null || longitude == null) return
    setLocation((prev) => ({ ...prev, latitude, longitude }))
    if (markerRef.current && mapInstanceRef.current && window.google?.maps) {
      const nextLatLng = new google.maps.LatLng(latitude, longitude)
      markerRef.current.setPosition(nextLatLng)
      mapInstanceRef.current.panTo(nextLatLng)
    }
  }, [latitude, longitude])

  useEffect(() => {
    if (!mapInstanceRef.current || !window.google?.maps) return
    mapInstanceRef.current.setMapTypeId(mapType)
  }, [mapType])

  return (
    <div className="space-y-4">
      <div className="relative h-80 w-full overflow-hidden rounded-lg border border-secondary-200">
        <div ref={mapContainerRef} className="h-full w-full" />
        {isLoadingMap && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/80">
            <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
          </div>
        )}
        {error && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-danger-50 text-danger-700">
            <AlertCircle className="mb-2 h-6 w-6" />
            <p className="text-sm font-medium">{error}</p>
          </div>
        )}
        {!error && (
          <div className="absolute inset-x-0 top-3 flex justify-end px-3">
            <div className="pointer-events-auto flex items-center gap-2 rounded-full border border-secondary-200 bg-white/90 px-2 py-1 shadow-md backdrop-blur">
              <div className="flex items-center gap-2 px-2 py-1 text-xs font-semibold text-secondary-600">
                <Layers className="h-4 w-4 text-primary-600" />
                Map Style
              </div>
              {[
                { id: 'roadmap' as google.maps.MapTypeId, label: 'Default' },
                { id: 'satellite' as google.maps.MapTypeId, label: 'Satellite' },
                { id: 'terrain' as google.maps.MapTypeId, label: 'Terrain' },
              ].map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setMapType(option.id)}
                  className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                    mapType === option.id
                      ? 'bg-primary-600 text-white shadow'
                      : 'text-secondary-600 hover:bg-secondary-100'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:items-end">
          <div>
            <label className="text-xs font-medium uppercase text-secondary-500">Latitude</label>
            <div className="relative mt-1">
              <MapPin className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-primary-600" />
              <input
                value={manualLatitude}
                onChange={(event) => setManualLatitude(event.target.value)}
                onBlur={applyManualCoordinates}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault()
                    applyManualCoordinates()
                  }
                }}
                inputMode="decimal"
                placeholder="Decimal degrees (e.g., 38.722300)"
                className="form-input pl-10"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium uppercase text-secondary-500">Longitude</label>
            <div className="relative mt-1">
              <MapPin className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 rotate-90 text-primary-600" />
              <input
                value={manualLongitude}
                onChange={(event) => setManualLongitude(event.target.value)}
                onBlur={applyManualCoordinates}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault()
                    applyManualCoordinates()
                  }
                }}
                inputMode="decimal"
                placeholder="Decimal degrees (e.g., -9.139300)"
                className="form-input pl-10"
              />
            </div>
          </div>

          <div className="flex flex-col gap-2 md:col-span-2 md:flex-row md:items-center md:justify-between">
            <p className="text-xs text-secondary-500">
              Enter latitude and longitude in decimal degrees to reposition the marker with precision.
            </p>
            <button
              type="button"
              onClick={applyManualCoordinates}
              className="btn-secondary inline-flex items-center gap-2 px-4 py-2"
            >
              Apply Coordinates
            </button>
          </div>
        </div>

        {manualError && (
          <div className="rounded-lg border border-danger-200 bg-danger-50 px-3 py-2 text-xs text-danger-700">
            {manualError}
          </div>
        )}

        {geocodeWarning && (
          <div className="flex items-start gap-2 rounded-lg border border-warning-200 bg-warning-50 px-3 py-2 text-xs text-warning-800">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <p>{geocodeWarning}</p>
          </div>
        )}
      </div>

      {isReverseGeocoding && (
        <div className="flex items-center gap-2 text-xs text-secondary-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Retrieving address details…
        </div>
      )}

      <div className="flex flex-col gap-3 border-t border-secondary-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-secondary-500">Drag the marker or click the map to reposition the project. Address, city and district are edited in Project Details.</p>
        <button
          type="button"
          onClick={onSave}
          className="btn-primary inline-flex items-center gap-2 px-4 py-2"
        >
          <Save className="h-4 w-4" />
          Save Location
        </button>
      </div>
    </div>
  )
}

export default ProjectLocationEditor

